import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, MaskReveal, Overline } from "@/components/Reveal";
import { PROGRAMS, STATS, TESTIMONIALS, PARTNERS } from "@/data/content";

const ROTATING = ["see them.", "access them.", "step into them."];

const Hero = () => {
  const [idx, setIdx] = useState(0);
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 200]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ROTATING.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative h-[100svh] min-h-[600px] md:min-h-[720px] overflow-hidden bg-[#0B132B]" data-testid="home-hero">
      <motion.div style={{ y: yBg }} className="absolute inset-x-0 top-0 -bottom-[15%] md:bottom-0 md:origin-top md:scale-110">
        <img
          src="https://images.pexels.com/photos/8555016/pexels-photo-8555016.jpeg?auto=compress&cs=tinysrgb&w=1920"
          srcSet="https://images.pexels.com/photos/8555016/pexels-photo-8555016.jpeg?auto=compress&cs=tinysrgb&w=800 800w, https://images.pexels.com/photos/8555016/pexels-photo-8555016.jpeg?auto=compress&cs=tinysrgb&w=1280 1280w, https://images.pexels.com/photos/8555016/pexels-photo-8555016.jpeg?auto=compress&cs=tinysrgb&w=1920 1920w"
          sizes="100vw"
          alt="Diverse young students smiling together"
          className="w-full h-full object-cover object-[65%_15%] md:object-[center_top]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B132B]/75 via-[#0B132B]/60 to-[#0B132B]/90" />
      </motion.div>

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-center pt-20 md:pt-0">
        <MaskReveal delay={0.1}>
          <p className="text-[#FFB323] text-xs md:text-sm uppercase tracking-[0.3em] font-bold mb-6">
            Inspired By You — Mentoring for Culturally diverse Students
          </p>
        </MaskReveal>
        <h1 className="font-display font-semibold text-white text-[2.35rem] sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] max-w-5xl">
          <MaskReveal delay={0.25}>They don't just dream</MaskReveal>
          <MaskReveal delay={0.4}>about careers — they</MaskReveal>
          <span className="block overflow-hidden h-[1.15em]">
            <AnimatePresence mode="wait">
              <motion.span
                key={idx}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                exit={{ y: "-110%" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="block text-[#FF5A36]"
              >
                {ROTATING[idx]}
              </motion.span>
            </AnimatePresence>
          </span>
        </h1>
        <MaskReveal delay={0.6}>
          <p className="text-white/80 text-base md:text-lg max-w-xl mt-8 leading-relaxed">
            IBY connects culturally diverse and disadvantaged students to fulfilling careers through expert,
            tailored, in-person mentoring. Real access — not just inspiration.
          </p>
        </MaskReveal>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap gap-4 mt-10"
        >
          <Link
            to="/contact"
            data-testid="hero-become-mentor-btn"
            className="rounded-full bg-[#FF5A36] text-white px-8 py-4 font-bold hover:bg-[#E04825] transition-colors duration-300 flex items-center gap-2"
          >
            Become a Mentor <ArrowRight size={18} />
          </Link>
          <a
            href="https://buy.stripe.com/8x2eVcfpM86T3bwex81oI01"
            data-testid="hero-support-student-btn"
            className="rounded-full border-2 border-white/60 text-white px-8 py-4 font-bold hover:bg-white hover:text-[#0B132B] transition-colors duration-300"
          >
            Support a Student
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div className="bg-[#FAF8F5] py-10 border-b border-black/5 overflow-hidden" data-testid="editorial-marquee">
    <Marquee speed={35} gradient={false} autoFill style={{ overflow: "hidden" }}>
      <span className="font-display font-semibold text-5xl md:text-7xl tracking-tight mx-8 text-outline whitespace-nowrap">
        Real access — not just inspiration.
      </span>
      <span className="font-display font-semibold text-5xl md:text-7xl tracking-tight mx-8 text-[#FF5A36] whitespace-nowrap">
        ✦
      </span>
    </Marquee>
  </div>
);

const GAP_POINTS = [
  { n: "01", title: "Limited pathways", text: "Careers advice built for the 'default' student leaves culturally diverse young people navigating an unfamiliar system alone — often as the first in their family to try." },
  { n: "02", title: "No relatable role models", text: "You can't be what you can't see. When no one in your world works in the careers you're curious about, ambition stays abstract." },
  { n: "03", title: "Generic advice", text: "One-size-fits-all programs ignore identity, culture and lived experience — the very things that shape how a young person sees their future." },
];

const TheGap = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="home-gap-section">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <Reveal>
            <Overline>The Gap</Overline>
            <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight leading-tight text-[#0B132B]">
              Talent is everywhere. <br />
              <span className="text-[#FF5A36]">Access isn't.</span>
            </h2>
            <p className="mt-6 text-[#4A5568] leading-relaxed max-w-md">
              Culturally diverse and disadvantaged students carry extraordinary drive, resilience and multilingual
              skill — yet face barriers that generic programs never touch. IBY was built to close
              that gap, deliberately.
            </p>
            <img
              src="/images/the-gap-mentorship.png"
              alt="Mentor and student collaborating at a laptop"
              className="rounded-[2rem] mt-10 w-full max-w-md object-cover aspect-[4/3]"
            />
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-7 flex flex-col justify-center gap-4">
        {GAP_POINTS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.1}>
            <div className="group border-t border-black/10 py-10 flex gap-8 hover:bg-white rounded-2xl px-4 md:px-8 transition-colors duration-500">
              <span className="font-display font-semibold text-5xl text-outline shrink-0 group-hover:text-[#FF5A36] transition-colors duration-500" style={{ WebkitTextStroke: undefined }}>
                {p.n}
              </span>
              <div>
                <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-[#0B132B] mb-3">{p.title}</h3>
                <p className="text-[#4A5568] leading-relaxed">{p.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.3}>
          <div className="bg-[#0B132B] text-white rounded-[2rem] p-10 md:p-12 mt-6 relative overflow-hidden grain">
            <p className="text-[#FFB323] text-xs uppercase tracking-[0.25em] font-bold mb-4 relative z-10">How IBY changes that</p>
            <p className="font-display text-2xl md:text-3xl font-medium leading-snug relative z-10">
              Expert mentors who share the journey. Programs built around identity, not despite it.
              Real workplaces, real networks, real first days.
            </p>
            <Link to="/about" data-testid="gap-learn-more-link" className="inline-flex items-center gap-2 mt-8 text-[#FF5A36] font-bold hover:gap-4 transition-all duration-300 relative z-10">
              How we're different <ArrowUpRight size={18} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Journey = () => (
  <section className="bg-white py-24 md:py-32" data-testid="home-journey-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>The IBY Journey</Overline>
        <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#0B132B] max-w-2xl">
          Three programs. One continuous path from classroom to career.
        </h2>
      </Reveal>

      <div className="relative mt-20">
        <motion.div
          className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#FFB323] via-[#FF5A36] to-[#0B132B] origin-top"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.6, ease: "easeOut" }}
        />
        <div className="space-y-20 md:space-y-28">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.id} delay={0.1}>
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                <div className="[direction:ltr] relative">
                  <img src={p.img} alt={`${p.name} program`} className="rounded-[2rem] w-full aspect-[4/3] object-cover" />
                  <span
                    className="absolute -top-5 -left-3 md:-left-5 font-display font-bold text-white text-lg w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.num}
                  </span>
                </div>
                <div className="[direction:ltr]">
                  <p className="text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ color: p.color === "#0B132B" ? "#FF5A36" : p.color }}>
                    {p.stage}
                  </p>
                  <h3 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-[#0B132B]">{p.name}</h3>
                  <p className="font-display text-xl text-[#FF5A36] mt-2 font-medium">{p.tagline}</p>
                  <p className="mt-5 text-[#4A5568] leading-relaxed">{p.delivers}</p>
                  <Link
                    to="/services"
                    data-testid={`journey-${p.id}-link`}
                    className="inline-flex items-center gap-2 mt-6 font-bold text-[#0B132B] hover:text-[#FF5A36] hover:gap-4 transition-all duration-300"
                  >
                    Explore {p.name} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="home-stats-section">
    <Reveal>
      <Overline>Impact</Overline>
      <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#0B132B] max-w-xl">
        Numbers with names behind them.
      </h2>
    </Reveal>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
      {STATS.map((s, i) => {
        const accent = i === 0 ? "orange" : i === 5 ? "ink" : "paper";
        return (
          <Reveal key={s.label} delay={i * 0.1}>
            <div
              className={`rounded-[2rem] p-8 md:p-10 h-full min-h-[220px] flex flex-col justify-between transition-transform duration-500 hover:-translate-y-2 ${
                accent === "orange"
                  ? "bg-[#FF5A36] text-white"
                  : accent === "ink"
                    ? "bg-[#0B132B] text-white"
                    : "bg-white border border-black/5"
              }`}
              data-testid={`stat-card-${i}`}
            >
              <span className={`font-display font-bold text-5xl md:text-6xl tracking-tight ${accent === "paper" ? "text-[#0B132B]" : ""}`}>
                {s.value}
              </span>
              <div>
                <p className={`font-bold text-lg ${accent === "paper" ? "text-[#0B132B]" : ""}`}>{s.label}</p>
                {s.note ? (
                  <p className={`text-sm ${accent === "paper" ? "text-[#4A5568]" : "text-white/70"}`}>{s.note}</p>
                ) : null}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const TestimonialCarousel = () => {
  const featured = TESTIMONIALS.slice(0, 4);
  const [i, setI] = useState(0);
  const t = featured[i];

  return (
    <section className="bg-[#0B132B] text-white py-24 md:py-32 relative grain overflow-hidden" data-testid="home-testimonials-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 relative z-10">
        <Reveal>
          <Overline light>Voices</Overline>
        </Reveal>
        <Quote size={72} className="text-[#FF5A36] mb-8 fill-[#FF5A36]" />
        <div className="min-h-[260px] md:min-h-[220px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="font-display font-medium text-2xl md:text-4xl tracking-tight leading-snug max-w-4xl">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-4 mt-10">
                <div>
                  <p className="font-bold">{t.name}</p>
                  <p className="text-white/60 text-sm">{t.role}</p>
                </div>
              </div>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="flex items-center justify-between mt-12">
          <div className="flex gap-3">
            <button
              onClick={() => setI((i - 1 + featured.length) % featured.length)}
              data-testid="testimonial-prev-btn"
              aria-label="Previous testimonial"
              className="w-12 h-12 rounded-full border border-white/25 flex items-center justify-center hover:bg-[#FF5A36] hover:border-[#FF5A36] transition-colors duration-300"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => setI((i + 1) % featured.length)}
              data-testid="testimonial-next-btn"
              aria-label="Next testimonial"
              className="w-12 h-12 rounded-full border border-white/25 flex items-center justify-center hover:bg-[#FF5A36] hover:border-[#FF5A36] transition-colors duration-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <Link to="/testimonials" data-testid="all-testimonials-link" className="text-[#FFB323] font-bold text-sm flex items-center gap-2 hover:gap-4 transition-all duration-300">
            All testimonials <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const PartnerStrip = () => (
  <section className="bg-white py-20 border-b border-black/5" data-testid="home-partner-strip">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-center text-xs uppercase tracking-[0.3em] font-bold text-[#4A5568] mb-12">
          Backed by partners who open real doors
        </p>
      </Reveal>
      <Marquee speed={30} gradient gradientColor="#ffffff" autoFill>
        {PARTNERS.map((p) => (
          <div key={p.name} className="mx-16 flex items-center">
            <img src={p.logo} alt={`${p.name} logo`} className="h-14 w-auto object-contain grayscale hover:grayscale-0 transition-[filter] duration-300" />
          </div>
        ))}
      </Marquee>
    </div>
  </section>
);

const ClosingCTA = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="home-closing-cta">
    <Reveal>
      <div className="relative rounded-[2.5rem] overflow-hidden bg-[#FF5A36] text-white p-12 md:p-20 grain">
        <div className="relative z-10 max-w-3xl">
          <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.08]">
            Every student deserves someone in their corner. Be that someone.
          </h2>
          <p className="mt-6 text-white/85 text-lg leading-relaxed max-w-xl">
            One hour a week. One door opened. One trajectory changed — for a young person, their
            family, and everyone who comes after them.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link
              to="/contact"
              data-testid="closing-cta-mentor-btn"
              className="rounded-full bg-[#0B132B] text-white px-8 py-4 font-bold hover:bg-black transition-colors duration-300 flex items-center gap-2"
            >
              Become a Mentor <ArrowRight size={18} />
            </Link>
            <Link
              to="/partners"
              data-testid="closing-cta-partner-btn"
              className="rounded-full border-2 border-white text-white px-8 py-4 font-bold hover:bg-white hover:text-[#FF5A36] transition-colors duration-300"
            >
              Partner With Us
            </Link>
          </div>
        </div>
        <span className="hidden lg:block absolute -right-10 -bottom-16 font-display font-bold text-[16rem] leading-none text-white/10 select-none">
          IBY
        </span>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <TheGap />
      <Journey />
      <Stats />
      <TestimonialCarousel />
      <PartnerStrip />
      <ClosingCTA />
    </main>
  );
}
